import { FC, useContext, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import { faUpload, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { uploadDocument } from 'services/myOrders';
import { showError, showSuccess, showWarning } from 'utils/utils';
import withErrorBoundary from '../../withErrorBoundary';

interface MoUploadDocumentProps {
  myOrder: MyOrder;
  onSuccess: (res: MyOrder) => void;
}

const MoUploadDocument: FC<MoUploadDocumentProps> = ({ myOrder, onSuccess }) => {
  const { t } = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === myOrder.fullSegment.orderSegId ||
    activeOrder?.orderRow.segmentId === myOrder.orderRow.segmentId;
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<Record<number, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedExtensions = ['.doc', '.docx', '.pdf', '.jpeg', '.jpg', '.png', '.gif'];
  const allowedMimeTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
  ];
  const maxSizeBytes = 3 * 1024 * 1024; // 3MB

  const getFileExtension = (fileName: string): string => {
    return fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  };

  const validateFile = (file: File): string | null => {
    const fileExtension = getFileExtension(file.name);
    if (!allowedExtensions.includes(fileExtension)) {
      return `Invalid file type for "${file.name}". Allowed types: ${allowedExtensions.join(', ')}`;
    }

    if (file.size > maxSizeBytes) {
      return `File "${file.name}" exceeds 3MB. Current size: ${(file.size / 1024 / 1024).toFixed(
        2
      )}MB`;
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (!files || files.length === 0) {
      setSelectedFiles([]);
      return;
    }

    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const error = validateFile(file);
      if (error) {
        validationErrors.push(error);
      } else {
        validFiles.push(file);
      }
    }

    if (validationErrors.length > 0) {
      showError(validationErrors.join('\n'));
    }

    setSelectedFiles(validFiles);
    const names: Record<number, string> = {};
    validFiles.forEach((file, index) => {
      names[index] = file.name;
    });
    setFileNames(names);
  };

  useOrdersAction(
    (action) =>
      action.action === 'upload-document' &&
      actionTargetsOrder(
        action,
        {
          segmentId: myOrder.orderRow.segmentId,
          altSegmentId: myOrder.fullSegment.orderSegId,
          orderId: myOrder.orderRow.orderId,
          altOrderId: myOrder.fullSegment.orderId,
        },
        isActive,
      ),
    () => {
      fileInputRef.current?.click();
    },
    isActive,
  );

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      showWarning('Files not selected');
      return;
    }

    setLoading(true);

    try {
      const renamedFiles = selectedFiles.map((file, index) => {
        const newName = fileNames[index] || file.name;

        return new File([file], newName, { type: file.type, lastModified: file.lastModified });
      });

      const params = {
        files: renamedFiles,
        orderId: myOrder.fullSegment.orderId,
        segmentId: myOrder.fullSegment.orderSegId,
      };

      const filesResponse = await uploadDocument(params);
      if (filesResponse.length) {
        showSuccess(`${selectedFiles.length} files uploaded successfully`);
        onSuccess({
          ...myOrder,
          order: { ...myOrder.order, documents: [...myOrder.order.documents, ...filesResponse] },
        });
        setSelectedFiles([]);
        setFileNames({});
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      showError(t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={allowedMimeTypes.join(',')}
          onChange={handleFileSelect}
          multiple
          style={{ display: 'none' }}
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          title="Choose files"
          className="btn btn-default btn-ssm mb-10"
          icon={faUpload}
          iconclassname="mr-5"
        />
        {selectedFiles.length > 0 && (
          <Button
            onClick={handleSubmit}
            title={loading ? 'Loading...' : 'Submit'}
            className="btn btn-primary btn-ssm mb-10"
            disabled={loading}
            icon={faCheck}
            iconclassname="mr-5"
          />
        )}
      </div>
      {selectedFiles.length > 0 && (
        <div className="flex-column flex-gap-10">
          {selectedFiles.map((file, index) => {
            const getFileNameWithoutExtension = (fileName: string): string => {
              const lastDotIndex = fileName.lastIndexOf('.');
              return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
            };

            const currentFileName = fileNames[index] || file.name;
            const nameWithoutExtension = getFileNameWithoutExtension(currentFileName);

            return (
              <div style={{ maxWidth: '300px' }}>
                <FormGroup
                  key={index}
                  value={nameWithoutExtension}
                  onChange={(name) => {
                    const originalExtension = file.name.substring(file.name.lastIndexOf('.'));
                    setFileNames((prev) => ({
                      ...prev,
                      [index]: name + originalExtension,
                    }));
                  }}
                  label=""
                  movablePlaceholder={`File ${index + 1}`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(MoUploadDocument, 'MoUploadDocument');
